import {useState} from "react";

export const getModalStorageKey = (userId: string, storageKey: string, version: string): string => {
    return `${userId}_${storageKey}_v${version}`;
};

export const markModalAsSeen = (userId: string, storageKey: string, version: string): void => {
    localStorage.setItem(getModalStorageKey(userId, storageKey, version), Date.now().toString());
};

export const shouldShowModal = (
    userId: string,
    storageKey: string,
    version: string,
    cooldownMs?: number,
): boolean => {
    const raw = localStorage.getItem(getModalStorageKey(userId, storageKey, version));
    if (!raw) {
        return true;
    }
    if (cooldownMs == null) {
        return false;
    }
    const dismissedAt = Number(raw);
    if (Number.isNaN(dismissedAt)) {
        return false;
    }
    return Date.now() - dismissedAt >= cooldownMs;
};

export const RELEASES_MODAL_VERSION = "3.2.0";
export const RELEASES_MODAL_STORAGE_KEY = "releases_modal_seen";

export const getReleaseStorageKey = (userId: string): string => {
    return getModalStorageKey(userId, RELEASES_MODAL_STORAGE_KEY, RELEASES_MODAL_VERSION);
};

export const hasUserSeenRelease = (userId: string): boolean => {
    const key = getReleaseStorageKey(userId);
    return localStorage.getItem(key) === "true";
};

export const shouldShowReleasesModal = (userId: string): boolean => {
    return !hasUserSeenRelease(userId);
};

export const markReleaseAsSeen = (userId: string): void => {
    const key = getReleaseStorageKey(userId);
    localStorage.setItem(key, "true");
};

export const useModalReleases = () => {
    const [visibleModalReleases, setVisibleModalReleases] = useState<boolean>(false);

    return {
        visibleModalReleases,
        setVisibleModalReleases,
    };
};

export const APP_MODAL_VERSION = "1.0.0";
export const APP_MODAL_STORAGE_KEY = "app_modal_seen";
export const APP_MODAL_COOLDOWN_MS = 48 * 60 * 60 * 1000;

export const shouldShowAppModal = (userId: string): boolean => {
    return shouldShowModal(
        userId,
        APP_MODAL_STORAGE_KEY,
        APP_MODAL_VERSION,
        APP_MODAL_COOLDOWN_MS,
    );
};

export const markAppModalAsSeen = (userId: string): void => {
    markModalAsSeen(userId, APP_MODAL_STORAGE_KEY, APP_MODAL_VERSION);
};

export const useModalApp = () => {
    const [visibleModalApp, setVisibleModalApp] = useState<boolean>(false);

    return {
        visibleModalApp,
        setVisibleModalApp,
    };
};

export const INTERNAL_CHAT_MODAL_VERSION = "1.0.0";
export const INTERNAL_CHAT_MODAL_STORAGE_KEY = "internal_chat_modal_seen";
export const INTERNAL_CHAT_MODAL_COOLDOWN_MS = 48 * 60 * 60 * 1000;

export const shouldShowInternalChatModal = (userId: string): boolean => {
    return shouldShowModal(
        userId,
        INTERNAL_CHAT_MODAL_STORAGE_KEY,
        INTERNAL_CHAT_MODAL_VERSION,
        INTERNAL_CHAT_MODAL_COOLDOWN_MS,
    );
};

export const markInternalChatModalAsSeen = (userId: string): void => {
    markModalAsSeen(userId, INTERNAL_CHAT_MODAL_STORAGE_KEY, INTERNAL_CHAT_MODAL_VERSION);
};

export const useModalInternalChat = () => {
    const [visibleModalInternalChat, setVisibleModalInternalChat] = useState<boolean>(false);

    return {
        visibleModalInternalChat,
        setVisibleModalInternalChat,
    };
};
